import {
  MatchReplaceRuleToolInputSchema,
  MATCH_REPLACE_RULE_TOOL_NAME,
} from '@terra/shared-dto-chat-types';
import { syncMatchReplaceRulesToElectronProxy } from '../../../../utils/matchReplaceProxySync';
import { readProxyStatusSafely } from '../../../../utils/readProxyStatusSafely';
import {
  buildMatchReplaceRuleFromToolInput,
  filterMatchReplaceRulesForList,
  mapMatchReplaceRuleToSummary,
} from '../../../../utils/matchReplaceToolRule.utils';
import { persistCurrentMatchReplaceRulesToServer } from '../../../../utils/persistMatchReplaceRules';
import { useProxyStore } from '../../../../store/proxy-store';
import {
  parsePortalToolHandlerInput,
  resolvePortalApplicationId,
} from '../portal-tool-handler.utils';
import type { PortalToolHandler, PortalToolHandlerResult } from '../types';

function getMatchReplaceStateSnapshot() {
  const proxyStore = useProxyStore.getState();

  return {
    engineEnabled: true,
    ruleCount: (proxyStore.settings.matchReplaceRules ?? []).length,
  };
}

async function finalizeMatchReplaceMutation({
  applicationId,
  message,
  ruleId,
  ruleName,
}: {
  applicationId: string;
  message: string;
  ruleId?: string;
  ruleName?: string;
}): Promise<PortalToolHandlerResult> {
  const persistResult = await persistCurrentMatchReplaceRulesToServer({
    applicationId,
    skipSelectedApplicationCheck: true,
  });

  if (!persistResult.success) {
    return {
      status: 'error',
      result: {
        message: persistResult.error,
      },
    };
  }

  const { isProxyRunning: storeProxyRunning } = useProxyStore.getState();
  const proxyStatus = await readProxyStatusSafely();
  const isProxyRunning = proxyStatus?.isRunning ?? storeProxyRunning;

  if (isProxyRunning) {
    const syncResult = await syncMatchReplaceRulesToElectronProxy();

    if (!syncResult.success) {
      return {
        status: 'error',
        result: {
          message:
            syncResult.error ?? 'Failed to sync match/replace rules to proxy',
        },
      };
    }
  }

  const snapshot = getMatchReplaceStateSnapshot();

  return {
    status: 'success',
    result: {
      message,
      data: {
        ruleId,
        ruleName,
        engineEnabled: snapshot.engineEnabled,
        ruleCount: snapshot.ruleCount,
      },
    },
  };
}

export const matchReplaceRuleHandler: PortalToolHandler = async ({
  input,
  toolCallId,
  batchId,
  applicationId,
}) => {
  const parsedInput = parsePortalToolHandlerInput({
    schema: MatchReplaceRuleToolInputSchema,
    input,
    toolName: MATCH_REPLACE_RULE_TOOL_NAME,
    toolCallId,
    batchId,
  });

  if (!parsedInput.ok) {
    return parsedInput.result;
  }

  const resolvedApplicationId = resolvePortalApplicationId({ applicationId });

  const proxyStore = useProxyStore.getState();

  switch (parsedInput.data.action) {
    case 'list': {
      const allRules = proxyStore.settings.matchReplaceRules ?? [];
      const filteredRules = filterMatchReplaceRulesForList({
        rules: allRules,
        nameFilter: parsedInput.data.nameFilter,
      });
      const snapshot = getMatchReplaceStateSnapshot();
      const rules = filteredRules.map(mapMatchReplaceRuleToSummary);
      const filterSuffix = parsedInput.data.nameFilter?.trim()
        ? ` matching "${parsedInput.data.nameFilter.trim()}"`
        : '';

      return {
        status: 'success',
        result: {
          message:
            rules.length === 0
              ? `No match/replace rules found${filterSuffix}`
              : `Listed ${rules.length} match/replace rule${rules.length === 1 ? '' : 's'}${filterSuffix}`,
          data: {
            engineEnabled: snapshot.engineEnabled,
            ruleCount: snapshot.ruleCount,
            rules,
          },
        },
      };
    }

    case 'add': {
      if (!parsedInput.data.rule) {
        return {
          status: 'error',
          result: { message: 'Rule is required for add action' },
        };
      }

      const rule = buildMatchReplaceRuleFromToolInput({
        ruleInput: parsedInput.data.rule,
      });

      proxyStore.addMatchReplaceRule(rule);

      return finalizeMatchReplaceMutation({
        applicationId: resolvedApplicationId,
        message: `Added match/replace rule "${rule.name}"`,
        ruleId: rule.id,
        ruleName: rule.name,
      });
    }

    case 'update': {
      if (!parsedInput.data.ruleId || !parsedInput.data.rule) {
        return {
          status: 'error',
          result: {
            message: 'ruleId and rule are required for update action',
          },
        };
      }

      const existingRule = proxyStore.settings.matchReplaceRules?.find(
        (rule) => rule.id === parsedInput.data.ruleId,
      );

      if (!existingRule) {
        return {
          status: 'error',
          result: {
            message: `Match/replace rule ${parsedInput.data.ruleId} not found`,
          },
        };
      }

      const updates = buildMatchReplaceRuleFromToolInput({
        ruleInput: parsedInput.data.rule,
        existingRule,
      });

      proxyStore.updateMatchReplaceRule(parsedInput.data.ruleId, {
        ...updates,
        id: parsedInput.data.ruleId,
        updatedAt: new Date(),
      });

      return finalizeMatchReplaceMutation({
        applicationId: resolvedApplicationId,
        message: `Updated match/replace rule "${updates.name}"`,
        ruleId: parsedInput.data.ruleId,
        ruleName: updates.name,
      });
    }

    case 'remove': {
      if (!parsedInput.data.ruleId) {
        return {
          status: 'error',
          result: { message: 'ruleId is required for remove action' },
        };
      }

      const removedRule = proxyStore.settings.matchReplaceRules?.find(
        (rule) => rule.id === parsedInput.data.ruleId,
      );

      if (!removedRule) {
        return {
          status: 'error',
          result: {
            message: `Match/replace rule ${parsedInput.data.ruleId} not found`,
          },
        };
      }

      proxyStore.removeMatchReplaceRule(parsedInput.data.ruleId);

      return finalizeMatchReplaceMutation({
        applicationId: resolvedApplicationId,
        message: `Removed match/replace rule ${parsedInput.data.ruleId}`,
        ruleId: parsedInput.data.ruleId,
        ruleName: removedRule.name,
      });
    }

    case 'toggle-engine': {
      return {
        status: 'success',
        result: {
          message:
            'Match/replace engine is always on. Use per-rule enable/disable to control individual rules.',
          data: {
            engineEnabled: true,
            ruleCount: (proxyStore.settings.matchReplaceRules ?? []).length,
          },
        },
      };
    }

    default:
      return {
        status: 'error',
        result: {
          message: `Unknown action for ${MATCH_REPLACE_RULE_TOOL_NAME}`,
        },
      };
  }
};
